import {useState, useEffect} from "react";
import {useForm, Controller} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {Col, Row, DatePicker, TimePicker, Select, Radio} from "antd";
import dayjs from "dayjs";
import {useSelector} from "react-redux";
import Button from "~/components/Button";
import Loading from "~/components/Loading";
import {InputField, TextAreaField, SelectUserAll, SelectUsersMultiple} from "~/components/Forms";
import {useCan} from "~/hooks";
import {currentUserSelector} from "~/features/Auth/authSlice";
import {groupApi} from "~/api";

const DISABLED_HOURS = () => [0, 1, 2, 3, 4, 5, 6, 23];

const TRAINING_TYPES = [
	{label: 'Toàn bộ phòng kinh doanh', value: 'all'},
	{label: 'Theo phòng', value: 'by_group'},
	{label: 'Theo cấp', value: 'by_level'},
	{label: 'Theo người chỉ định', value: 'by_designation'},
];

const LEVEL_OPTIONS = [
	{label: 'Trưởng phòng', value: 'truong_phong'},
	{label: 'Quản lý', value: 'quan_ly'},
	{label: 'Nhóm trưởng', value: 'nhom_truong'},
];

function TrainingScheduleFormAddEdit({item, onHandleSubmit}) {

	const currentUser      = useSelector(currentUserSelector);
	const canSelectTrainer = useCan('trainingScheduleSelectTrainer');
	const isEdit           = Boolean(item?.id);

	const [groups, setGroups]               = useState([]);
	const [dateOpen, setDateOpen]           = useState(false);
	const [startHourOpen, setStartHourOpen] = useState(false);
	const [endHourOpen, setEndHourOpen]     = useState(false);

	useEffect(() => {
		groupApi.all().then(res => {
			if (res?.data) {
				setGroups(
					res.data
						.filter(g => g.totalUser > 0)
						.map(g => ({value: g.id, label: g.name}))
				);
			}
		});
	}, []);

	const trainerDefaultOptions = isEdit && item?.trainer
		? [{value: item.trainer.id, label: `${item.trainer.firstname} ${item.trainer.lastname}`}]
		: (currentUser && !canSelectTrainer
			? [{value: currentUser.id, label: `${currentUser.firstname} ${currentUser.lastname}`}]
			: []);

	const designeeDefaultOptions = isEdit && item?.designees?.length
		? item.designees.map(u => ({value: u.id, label: `${u.firstname} ${u.lastname}`}))
		: [];

	const validationSchema = Yup.object().shape({
		topic:     Yup.string().required('Chủ đề đào tạo là bắt buộc'),
		startDate: Yup.mixed().required('Ngày đào tạo là bắt buộc').nullable(),
		startHour: Yup.mixed().required('Giờ bắt đầu là bắt buộc').nullable(),
		endHour:   Yup.mixed()
			.required('Giờ kết thúc là bắt buộc')
			.nullable()
			.test('end-after-start', 'Giờ kết thúc phải sau giờ bắt đầu', function(value) {
				const {startHour} = this.parent;
				if (!startHour || !value) return true;
				const start = dayjs(startHour).hour() * 60 + dayjs(startHour).minute();
				const end   = dayjs(value).hour() * 60 + dayjs(value).minute();
				return end > start;
			})
			.test('max-4-hours', 'Thời gian đào tạo không được vượt quá 4 tiếng', function(value) {
				const {startHour} = this.parent;
				if (!startHour || !value) return true;
				const start = dayjs(startHour).hour() * 60 + dayjs(startHour).minute();
				const end   = dayjs(value).hour() * 60 + dayjs(value).minute();
				return (end - start) <= 4 * 60;
			}),
		trainerId: Yup.mixed().required('Người đào tạo là bắt buộc'),
		type:      Yup.string().required('Loại đào tạo là bắt buộc'),
		groupIds: Yup.array().when('type', {
			is:        'by_group',
			then:      (schema) => schema.min(1, 'Vui lòng chọn ít nhất một phòng'),
			otherwise: (schema) => schema.optional(),
		}),
		levels: Yup.array().when('type', {
			is:        'by_level',
			then:      (schema) => schema.min(1, 'Vui lòng chọn ít nhất một cấp'),
			otherwise: (schema) => schema.optional(),
		}),
		designeeIds: Yup.array().when('type', {
			is:        'by_designation',
			then:      (schema) => schema.min(1, 'Vui lòng chọn ít nhất một người'),
			otherwise: (schema) => schema.optional(),
		}),
	});

	const defaultValues = {
		topic:       '',
		content:     '',
		startDate:   null,
		startHour:   null,
		endHour:     null,
		trainerId:   canSelectTrainer ? undefined : currentUser?.id,
		type:        'all',
		groupIds:    [],
		levels:      [],
		designeeIds: [],
	};

	const {control, handleSubmit, formState: {isSubmitting, errors}, reset, watch, setValue} = useForm({
		defaultValues,
		resolver: yupResolver(validationSchema),
	});

	const watchType = watch('type');

	useEffect(() => {
		if (isEdit && item?.id) {
			const startDayjs = item.startTime ? dayjs.unix(item.startTime) : null;
			const endDayjs   = item.endTime   ? dayjs.unix(item.endTime)   : null;
			reset({
				topic:       item.topic || '',
				content:     item.content || '',
				startDate:   startDayjs,
				startHour:   startDayjs,
				endHour:     endDayjs,
				trainerId:   item.trainerId || undefined,
				type:        item.type || 'all',
				groupIds:    item.groupIds || [],
				levels:      item.levels || [],
				designeeIds: item.designeeIds || item.designees?.map(u => u.id) || [],
			});
		} else if (!isEdit) {
			reset({
				...defaultValues,
				trainerId: canSelectTrainer ? undefined : currentUser?.id,
			});
		}
	}, [item?.id]);

	const onSubmit = async (data) => {
		let startTimeUnix = null;
		let endTimeUnix   = null;

		if (data.startDate && data.startHour) {
			startTimeUnix = dayjs(data.startDate)
				.hour(dayjs(data.startHour).hour())
				.minute(dayjs(data.startHour).minute())
				.second(0)
				.unix();
		}
		if (data.startDate && data.endHour) {
			endTimeUnix = dayjs(data.startDate)
				.hour(dayjs(data.endHour).hour())
				.minute(dayjs(data.endHour).minute())
				.second(0)
				.unix();
		}

		const payload = {
			topic:       data.topic,
			content:     data.content,
			startTime:   startTimeUnix,
			endTime:     endTimeUnix,
			trainerId:   data.trainerId,
			type:        data.type,
			groupIds:    data.type === 'by_group'       ? data.groupIds    : [],
			levels:      data.type === 'by_level'       ? data.levels      : [],
			designeeIds: data.type === 'by_designation' ? data.designeeIds : [],
		};
		await onHandleSubmit(payload, item);
	};

	const handleTypeChange = () => {
		setValue('groupIds', []);
		setValue('levels', []);
		setValue('designeeIds', []);
	};

	return (
		<form className="form" onSubmit={handleSubmit(onSubmit)}>
			{isSubmitting && <Loading />}

			<Row gutter={10}>
				<Col span={24}>
					<Controller control={control} name="topic" render={({field}) => (
						<InputField label="Chủ đề đào tạo" errors={errors} {...field} />
					)} />
				</Col>
			</Row>

			<Row gutter={10}>
				<Col span={8}>
					<Controller control={control} name="startDate" render={({field}) => (
						<div className="form-group">
							<label>Ngày đào tạo</label>
							<DatePicker
								format="DD/MM/YYYY"
								value={field.value}
								open={dateOpen}
								onOpenChange={setDateOpen}
								onSelect={(date) => {
									field.onChange(date);
									setDateOpen(false);
								}}
								placeholder="Chọn ngày"
								popupStyle={{zIndex: 10000}}
								className={`form-control${errors.startDate ? ' error' : ''}`}
								style={{width: '100%'}}
							/>
							{errors.startDate && <p className="error-message">{errors.startDate.message}</p>}
						</div>
					)} />
				</Col>

				<Col span={8}>
					<Controller control={control} name="startHour" render={({field}) => (
						<div className="form-group">
							<label>Giờ bắt đầu</label>
							<TimePicker
								format="HH:mm"
								value={field.value}
								open={startHourOpen}
								onOpenChange={setStartHourOpen}
								onSelect={(time) => {
									field.onChange(time);
									setStartHourOpen(false);
								}}
								placeholder="Chọn giờ bắt đầu"
								popupStyle={{zIndex: 10000}}
								className={`form-control${errors.startHour ? ' error' : ''}`}
								style={{width: '100%'}}
								minuteStep={5}
								disabledTime={() => ({disabledHours: DISABLED_HOURS})}
								hideDisabledOptions
							/>
							{errors.startHour && <p className="error-message">{errors.startHour.message}</p>}
						</div>
					)} />
				</Col>

				<Col span={8}>
					<Controller control={control} name="endHour" render={({field}) => (
						<div className="form-group">
							<label>Giờ kết thúc</label>
							<TimePicker
								format="HH:mm"
								value={field.value}
								open={endHourOpen}
								onOpenChange={setEndHourOpen}
								onSelect={(time) => {
									field.onChange(time);
									setEndHourOpen(false);
								}}
								placeholder="Chọn giờ kết thúc"
								popupStyle={{zIndex: 10000}}
								className={`form-control${errors.endHour ? ' error' : ''}`}
								style={{width: '100%'}}
								minuteStep={5}
								disabledTime={() => ({disabledHours: DISABLED_HOURS})}
								hideDisabledOptions
							/>
							{errors.endHour && <p className="error-message">{errors.endHour.message}</p>}
						</div>
					)} />
				</Col>
			</Row>

			<Row gutter={10}>
				<Col span={24}>
					{canSelectTrainer ? (
						<Controller control={control} name="trainerId" render={({field}) => (
							<SelectUserAll
								label="Người đào tạo"
								options={trainerDefaultOptions}
								errors={errors}
								onlyActive={1}
								{...field}
							/>
						)} />
					) : (
						<div className="form-group">
							<label>Người đào tạo</label>
							<input
								className="form-control"
								value={`${currentUser?.firstname || ''} ${currentUser?.lastname || ''}`}
								disabled
								readOnly
							/>
						</div>
					)}
				</Col>
			</Row>

			<Row gutter={10}>
				<Col span={24}>
					<Controller control={control} name="type" render={({field}) => (
						<div className="form-group">
							<label>Đối tượng đào tạo</label>
							<div style={{paddingTop: 6}}>
								<Radio.Group
									options={TRAINING_TYPES}
									value={field.value}
									onChange={(e) => {
										field.onChange(e.target.value);
										handleTypeChange();
									}}
								/>
							</div>
							{errors.type && <p className="error-message">{errors.type.message}</p>}
						</div>
					)} />
				</Col>
			</Row>

			{watchType === 'by_group' && (
				<Row gutter={10}>
					<Col span={24}>
						<Controller control={control} name="groupIds" render={({field}) => (
							<div className="form-group">
								<label>Chọn phòng đào tạo</label>
								<Select
									mode="multiple"
									options={groups}
									value={field.value}
									onChange={field.onChange}
									placeholder="Chọn phòng..."
									style={{width: '100%'}}
									popupMatchSelectWidth={false}
									className={errors.groupIds ? 'error' : ''}
								/>
								{errors.groupIds && <p className="error-message">{errors.groupIds.message}</p>}
							</div>
						)} />
					</Col>
				</Row>
			)}

			{watchType === 'by_level' && (
				<Row gutter={10}>
					<Col span={24}>
						<Controller control={control} name="levels" render={({field}) => (
							<div className="form-group">
								<label>Chọn cấp đào tạo</label>
								<Select
									mode="multiple"
									options={LEVEL_OPTIONS}
									value={field.value}
									onChange={field.onChange}
									placeholder="Chọn cấp..."
									style={{width: '100%'}}
									className={errors.levels ? 'error' : ''}
								/>
								{errors.levels && <p className="error-message">{errors.levels.message}</p>}
							</div>
						)} />
					</Col>
				</Row>
			)}

			{watchType === 'by_designation' && (
				<Row gutter={10}>
					<Col span={24}>
						<Controller control={control} name="designeeIds" render={({field}) => (
							<SelectUsersMultiple
								label="Người được đào tạo"
								options={designeeDefaultOptions}
								errors={errors}
								onlyActive={1}
								placeholder="Tìm và chọn người..."
								{...field}
							/>
						)} />
					</Col>
				</Row>
			)}

			<Row gutter={10}>
				<Col span={24}>
					<Controller control={control} name="content" render={({field}) => (
						<TextAreaField label="Nội dung đào tạo" errors={errors} rows={4} {...field} />
					)} />
				</Col>
			</Row>

			<hr />
			<div className="form-group d-flex justify-content-end modal-bottom">
				<Button primary type="submit">
					{isEdit ? 'Cập nhật' : 'Tạo'} lịch đào tạo
				</Button>
			</div>
		</form>
	);
}

export default TrainingScheduleFormAddEdit;
